import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  clearEmbedToken,
  setEmbedToken,
  setEmbedUnauthorizedHook,
} from "../api/api";
import { decodeJwtPayload } from "./AuthContext";

// Module 11 — Iframe embed surface.
//
// EmbedTokenContext is the single source of truth for auth state inside
// `/embed/*` routes. Token lifecycle:
//   1. EmbedRoot mounts → reads `?token=<jwt>` from URL.
//   2. Decodes the JWT, populates `claims` in context for downstream
//      pages (organization_id, partner_id, sub, exp, ...).
//   3. Hands the token to `setEmbedToken()` so axios picks it up on
//      the next request. NEVER touches localStorage (§3.2 mitigation
//      against URL-borne token leakage).
//   4. `history.replaceState`s the URL to drop the `?token=` query
//      param so it doesn't end up in browser history / Referer.
//   5. Wires a 401 hook so any tenant-API 401 inside the iframe
//      bubbles up to the parent frame as a postMessage instead of
//      hijacking the page (we don't own the redirect target — the
//      iframe lives in someone else's app).
//
// Token disappears on unmount. That's correct for an iframe session.
const EmbedTokenContext = createContext({
  token: null,
  claims: null,
  ready: false,
  error: null,
});

// Allowed parent origins for `postMessage` calls. We use "*" deliberately
// here because the parent's origin is partner-specific and is enforced
// SERVER-SIDE via `Content-Security-Policy: frame-ancestors`. The browser
// won't load the iframe at all unless the parent's origin is in
// `Partner.allowed_domains`, so by the time we postMessage we already
// know the parent is a trusted origin (whichever ones the partner
// whitelisted). If we wanted strict origin pinning here, we'd need
// each iframe to know its parent's origin a priori — which we don't.
const POST_MESSAGE_TARGET = "*";

// Lead time before JWT `exp` at which we emit `noticeai.token_expiring`.
// 60s gives the parent (e.g. ZOHO) enough time to mint a fresh JWT and
// re-render the iframe with `?token=<new>` before the current session
// dies. Spec MODULE 11 §4.3 item 2.
const EXPIRY_WARN_LEAD_MS = 60_000;

const sendToParent = (payload) => {
  if (typeof window === "undefined" || !window.parent) return;
  if (window.parent === window) return; // not in an iframe — noop
  try {
    window.parent.postMessage(payload, POST_MESSAGE_TARGET);
  } catch (e) {
    // Cross-origin restrictions can theoretically reject postMessage
    // even with "*"; swallow so we don't kill the embed render.
    console.warn("embed postMessage failed:", e?.message);
  }
};

export const EmbedTokenProvider = ({ children }) => {
  const location = useLocation();
  const [token, setTokenState] = useState(null);
  const [claims, setClaims] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  // True once `Date.now() > exp`. EmbedLayout reads this and renders
  // the "Session expired — close and reopen" overlay (§4.3 item 3).
  // The flag stays sticky — once expired, no further state mutation
  // unflips it; the iframe must be reloaded with a fresh `?token=`.
  const [tokenExpired, setTokenExpired] = useState(false);

  // Track whether we've consumed the `?token=` query param yet. After
  // the first `replaceState`, the URL won't have the query but the
  // token is in memory — re-running the bootstrap on every location
  // change would clobber it.
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    const params = new URLSearchParams(location.search);
    // Pull from URL first; fall back to sessionStorage (set on first
    // successful mount) so an in-iframe F5 can recover without the
    // parent re-rendering. Tab-scoped, cleared on tab close — same
    // pattern as `impersonator_original_token` in ImpersonateUserModal.
    const incoming = params.get("token") || sessionStorage.getItem("embed_token");
    if (!incoming) {
      // No token on URL. Could be: page refresh inside the iframe (token
      // already consumed), direct navigation without bootstrap, or genuine
      // misconfiguration. Auto-notify the parent so a properly-implemented
      // host (Zoho per spec §4.3) can re-render the iframe with a fresh
      // ?token=. The "Session expired" overlay still renders below as a
      // fallback for hosts that don't listen.
      sendToParent({ type: "noticeai.token_expired" });
      setReady(true);
      bootstrappedRef.current = true;
      return;
    }

    const decoded = decodeJwtPayload(incoming);
    if (!decoded) {
      setError("Invalid token format");
      setReady(true);
      bootstrappedRef.current = true;
      return;
    }

    // Hand to axios + populate state.
    setEmbedToken(incoming);
    sessionStorage.setItem("embed_token", incoming);
    setTokenState(incoming);
    setClaims(decoded);
    setReady(true);
    bootstrappedRef.current = true;

    // Drop the token from the URL. Use replaceState so this doesn't
    // create a history entry (browser back shouldn't re-trigger the
    // token consumption).
    params.delete("token");
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : "");
    try {
      window.history.replaceState({}, "", newUrl);
    } catch {
      // Some old browsers / sandbox modes may block history mutations
      // in iframes; failing silently is safer than crashing the embed.
    }
  }, [location.search]);

  // 401 → notify parent. Wired once, lasts the lifetime of the embed.
  useEffect(() => {
    setEmbedUnauthorizedHook(() => {
      sendToParent({ type: "noticeai.token_expired" });
    });
    return () => {
      setEmbedUnauthorizedHook(null);
    };
  }, []);

  // Expiry timers (Phase 3 §4.3 items 2 + 3). Two beats:
  //   T1 = exp - 60s → emit `noticeai.token_expiring` so the parent
  //                    can refresh the iframe with a new JWT.
  //   T2 = exp       → set `tokenExpired = true`. EmbedLayout reads
  //                    that and renders the "session expired" overlay.
  //                    Also clears the axios in-memory token so any
  //                    in-flight retries don't re-authenticate against
  //                    a JWT the server already considers stale.
  // Both timers are cancelled on unmount + when `claims.exp` changes
  // (i.e. when a new token is loaded, e.g. via remount with fresh
  // `?token=` from a parent-driven refresh).
  const expiresAtMs = claims?.exp ? claims.exp * 1000 : null;
  useEffect(() => {
    if (!expiresAtMs) return undefined;
    const now = Date.now();

    // If the token is already past `exp` at mount time (e.g. parent
    // handed us a stale JWT), flip the flag immediately and skip the
    // timers — there's nothing to warn about.
    if (now >= expiresAtMs) {
      setTokenExpired(true);
      clearEmbedToken();
      sessionStorage.removeItem("embed_token");
      return undefined;
    }

    const warnDelay = expiresAtMs - EXPIRY_WARN_LEAD_MS - now;
    const expireDelay = expiresAtMs - now;

    let warnTimer = null;
    let expireTimer = null;

    // Edge case: if the token has less than 60s of life remaining at
    // mount, fire the warning straight away rather than scheduling a
    // negative-delay timeout (some browsers fire those eagerly anyway,
    // but explicit is clearer).
    if (warnDelay <= 0) {
      sendToParent({
        type: "noticeai.token_expiring",
        expires_at: new Date(expiresAtMs).toISOString(),
      });
    } else {
      warnTimer = setTimeout(() => {
        sendToParent({
          type: "noticeai.token_expiring",
          expires_at: new Date(expiresAtMs).toISOString(),
        });
      }, warnDelay);
    }

    expireTimer = setTimeout(() => {
      setTokenExpired(true);
      clearEmbedToken();
      sessionStorage.removeItem("embed_token");
    }, expireDelay);

    return () => {
      if (warnTimer) clearTimeout(warnTimer);
      if (expireTimer) clearTimeout(expireTimer);
    };
  }, [expiresAtMs]);

  // Cleanup on full unmount: clear axios's reference so a stale token
  // can't outlive the iframe (defensive — iframe unmount usually means
  // the whole React tree is gone, but covers HMR and route swaps).
  useEffect(() => {
    return () => {
      clearEmbedToken();
    };
  }, []);

  // Hard 401 path used by pages that catch errors locally before the
  // axios interceptor sees them. Exported so `Embed*` pages can call
  // it explicitly when surfacing an expired-session UI.
  const notifyTokenExpired = useCallback(() => {
    sendToParent({ type: "noticeai.token_expired" });
  }, []);

  const value = useMemo(
    () => ({
      token,
      claims,
      ready,
      error,
      tokenExpired,
      partnerId: claims?.partner_id || null,
      organizationId: claims?.org_id || claims?.organization_id || null,
      userId: claims?.sub || null,
      expiresAt: expiresAtMs,
      notifyTokenExpired,
    }),
    [token, claims, ready, error, tokenExpired, expiresAtMs, notifyTokenExpired]
  );

  return (
    <EmbedTokenContext.Provider value={value}>
      {children}
    </EmbedTokenContext.Provider>
  );
};

export const useEmbedToken = () => useContext(EmbedTokenContext);

// Pure helper for tests / route guards: extract origin from a URL string.
// Exported because route guards may want to inspect the partner's
// allowed_domains client-side (defence-in-depth — server CSP is the real
// gate).
export const originFromUrl = (urlString) => {
  try {
    return new URL(urlString).origin;
  } catch {
    return null;
  }
};
